import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';
const REVIEW_API = process.env.REACT_APP_REVIEW_API_URL || 'http://localhost:8082';
const NOTIFICATION_API = process.env.REACT_APP_NOTIFICATION_API_URL || 'http://localhost:9050';

const PaperAssignment = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [papers, setPapers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [abstractMetadata, setAbstractMetadata] = useState(null);

  useEffect(() => {
    fetchActiveConferences();
    fetchReviewers();
  }, []);

  const fetchActiveConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setConferences(data);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchReviewers = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/api/submission-reviewers/role/REVIEWER`);
      if (response.ok) {
        const data = await response.json();
        setReviewers(data);
      }
    } catch (error) {
      console.error('Error fetching reviewers:', error);
    }
  };

  const fetchPapers = async (conferenceId) => {
    if (!conferenceId) return;

    setLoading(true);
    try {
      // Get conference name from selected conference
      const selectedConference = conferences.find(conf => conf.id === parseInt(conferenceId));
      
      if (!selectedConference) {
        showAlert('Conference not found', 'error');
        setLoading(false);
        return;
      }
      
      // Fetch abstract metadata to check visibility settings
      try {
        const metadataResponse = await fetch(`${CONFERENCE_API}/api/abstract-metadata/conference/${conferenceId}`);
        if (metadataResponse.ok) {
          const metadata = await metadataResponse.json();
          const metadataObj = Array.isArray(metadata) ? metadata[0] : metadata;
          setAbstractMetadata(metadataObj);
        }
      } catch (err) {
        console.error('Failed to fetch abstract metadata:', err);
        setAbstractMetadata(null);
      }
      if (!selectedConference) {
        showAlert('Conference not found', 'error');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${REGISTRATION_API}/api/papers/conference/${encodeURIComponent(selectedConference.name)}`);
      if (response.ok) {
        const papersData = await response.json();
        
        // Fetch reviewer assignment details for each paper from review management service
        const papersWithReviewerCounts = await Promise.all(
          papersData.map(async (paper) => {
            try {
              const assignmentsResponse = await fetch(`${REVIEW_API}/review/review-assignments/paper/${paper.id}`);
              if (assignmentsResponse.ok) {
                const assignments = await assignmentsResponse.json();
                
                // Fetch reviewer names for each assignment
                const reviewerNames = await Promise.all(
                  assignments.map(async (assignment) => {
                    try {
                      const reviewerResponse = await fetch(`${CONFERENCE_API}/api/submission-reviewers/${assignment.reviewerId}`);
                      if (reviewerResponse.ok) {
                        const reviewer = await reviewerResponse.json();
                        return reviewer.fullName || reviewer.email;
                      }
                    } catch (err) {
                      console.error(`Error fetching reviewer ${assignment.reviewerId}:`, err);
                    }
                    return `Reviewer ${assignment.reviewerId}`;
                  })
                );
                
                return { 
                  ...paper, 
                  assignedReviewersCount: assignments.length,
                  reviewerNames: reviewerNames
                };
              }
            } catch (error) {
              console.error(`Failed to fetch reviewer assignments for paper ${paper.id}:`, error);
            }
            return { ...paper, assignedReviewersCount: 0, reviewerNames: [] };
          })
        );
        
        setPapers(papersWithReviewerCounts);
      } else {
        setPapers([]);
        showAlert('Failed to fetch papers', 'error');
      }
    } catch (error) {
      console.error('Error fetching papers:', error);
      setPapers([]);
      showAlert('Error fetching papers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedConferenceId) {
      fetchPapers(selectedConferenceId);
    } else {
      setPapers([]);
    }
  }, [selectedConferenceId]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleOpenAssignModal = (paper) => {
    setSelectedPaper(paper);
    setSelectedReviewers(paper.assignedReviewers || []);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedPaper(null);
    setSelectedReviewers([]);
  };

  const handleReviewerToggle = (reviewerId) => {
    setSelectedReviewers(prev => {
      if (prev.includes(reviewerId)) {
        return prev.filter(id => id !== reviewerId);
      } else {
        return [...prev, reviewerId];
      }
    });
  };

  const handleAssignReviewers = async () => {
    if (selectedReviewers.length === 0) {
      showAlert('Please select at least one reviewer', 'error');
      return;
    }

    setLoading(true);
    try {
      // Papers already exist in Registration service, use paper ID directly
      const paperId = selectedPaper.id;
      console.log('Assigning reviewers for paper ID:', paperId);

      // Assign reviewers using review management service
      const assignResponse = await fetch(`${REVIEW_API}/review/review-assignments/batch?paperId=${encodeURIComponent(paperId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedReviewers)
      });

      if (assignResponse.ok) {
        // Step 3: Send email notifications to assigned reviewers
        const selectedConference = conferences.find(conf => conf.id === parseInt(selectedConferenceId));
        console.log('Starting to send emails to reviewers...');
        console.log('Selected reviewers:', selectedReviewers);
        console.log('Available reviewers:', reviewers);
        
        const emailPromises = selectedReviewers.map(async (reviewerId) => {
          const reviewer = reviewers.find(r => r.id === reviewerId);
          console.log(`Processing reviewer ID ${reviewerId}:`, reviewer);
          
          if (reviewer && reviewer.email) {
            try {
              // Check if reviewer needs to set password (firstLogin flag)
              const needsPasswordSetup = reviewer.firstLogin === true || !reviewer.password;
              
              const emailBody = `
                <html>
                  <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                    <h2 style="color: #2c3e50;">Paper Review Assignment</h2>
                    <p>Dear ${reviewer.fullName || 'Reviewer'},</p>
                    <p>You have been assigned to review a paper for <strong>${selectedConference?.name || 'the conference'}</strong>.</p>
                    ${needsPasswordSetup ? `
                    <div style="background-color: #fff3cd; padding: 15px; border-left: 4px solid #ff9800; margin: 20px 0;">
                      <h3 style="margin-top: 0; color: #ff9800;">⚠️ Action Required: Set Your Password</h3>
                      <p>Before you can access the review system, you need to set your password.</p>
                      <p>A separate email with password setup instructions has been sent to you. Please check your inbox (including spam folder) for the "Set Your Password" email.</p>
                      <p><strong>Note:</strong> The password setup link is valid for 24 hours.</p>
                    </div>
                    ` : ''}
                    <div style="background-color: #f8f9fa; padding: 15px; border-left: 4px solid #007bff; margin: 20px 0;">
                      <h3 style="margin-top: 0; color: #007bff;">Paper Details:</h3>
                      <p><strong>Title:</strong> ${selectedPaper.title}</p>
                      ${visibleToReviewers && selectedPaper.authorName ? `<p><strong>Author:</strong> ${selectedPaper.authorName}</p>` : ''}
                      <p><strong>Track:</strong> ${selectedPaper.trackName || 'Not specified'}</p>
                      <p><strong>Submission Date:</strong> ${new Date(selectedPaper.createdAt).toLocaleDateString()}</p>
                    </div>
                    <p>${needsPasswordSetup ? 'After setting your password, please' : 'Please'} log in to the review system to view the paper and submit your review.</p>
                    <p style="margin-top: 30px;">Best regards,<br/><strong>Conference Management Team</strong></p>
                  </body>
                </html>
              `;
              
              console.log(`Sending assignment email to ${reviewer.email}...`);
              
              // If reviewer needs password setup, resend the setup email first
              if (needsPasswordSetup && reviewer.passwordResetToken) {
                console.log(`Reviewer needs password setup, resending setup email...`);
                try {
                  const setupLink = `${window.location.origin}/set-password?token=${reviewer.passwordResetToken}`;
                  const setupEmailBody = `
                    <html>
                      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                        <h2 style="color: #2c3e50;">Set Your Password - Conference Review System</h2>
                        <p>Dear ${reviewer.fullName || 'Reviewer'},</p>
                        <p>You have been assigned to review papers, but first you need to set your password.</p>
                        <div style="background-color: #e7f3ff; padding: 15px; border-left: 4px solid #007bff; margin: 20px 0;">
                          <p><strong>Click the link below to set your password:</strong></p>
                          <p style="margin: 15px 0;">
                            <a href="${setupLink}" style="background-color: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block;">
                              Set Password
                            </a>
                          </p>
                          <p style="font-size: 12px; color: #666;">Or copy this link: ${setupLink}</p>
                        </div>
                        <p><strong>Note:</strong> This link is valid for 24 hours.</p>
                        <p>After setting your password, you'll receive another email with your paper assignment details.</p>
                        <p style="margin-top: 30px;">Best regards,<br/><strong>Conference Management Team</strong></p>
                      </body>
                    </html>
                  `;
                  
                  await fetch(`${NOTIFICATION_API}/notification/send-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      to: reviewer.email,
                      subject: 'Set Your Password - Conference Review System',
                      body: setupEmailBody
                    })
                  });
                  console.log(`✓ Password setup email sent to ${reviewer.email}`);
                } catch (setupError) {
                  console.error(`✗ Failed to send password setup email:`, setupError);
                }
              }
              
              // Send the assignment notification email
              const emailResponse = await fetch(`${NOTIFICATION_API}/notification/send-email`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  to: reviewer.email,
                  subject: `Paper Review Assignment - ${selectedConference?.name || 'Conference'}`,
                  body: emailBody
                })
              });
              
              if (emailResponse.ok) {
                console.log(`✓ Email sent successfully to ${reviewer.email}`);
              } else {
                const errorText = await emailResponse.text();
                console.error(`✗ Failed to send email to ${reviewer.email}:`, errorText);
              }
            } catch (emailError) {
              console.error(`✗ Error sending email to ${reviewer.email}:`, emailError);
            }
          } else {
            console.warn(`Skipping reviewer ID ${reviewerId}: ${!reviewer ? 'Not found' : 'No email address'}`);
          }
        });
        
        // Wait for all emails to be sent (but don't block on failures)
        await Promise.allSettled(emailPromises);
        console.log('All email notifications processed.');
        
        showAlert('Reviewers assigned successfully! Email notifications sent.', 'success');
        handleCloseAssignModal();
        fetchPapers(selectedConferenceId);
      } else {
        const errorText = await assignResponse.text();
        showAlert(errorText || 'Failed to assign reviewers', 'error');
      }
    } catch (error) {
      console.error('Error assigning reviewers:', error);
      showAlert('Error assigning reviewers: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'SUBMITTED': '#17a2b8',
      'UNDER_REVIEW': '#ffc107',
      'ACCEPTED': '#28a745',
      'REJECTED': '#dc3545',
      'REVISION_REQUESTED': '#fd7e14'
    };
    return colors[status] || '#6c757d';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>Paper Assignment</h2>
        <p style={{ color: '#666', marginTop: '8px' }}>
          Assign reviewers to submitted papers
        </p>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Conference:
        </label>
        <select
          value={selectedConferenceId}
          onChange={(e) => setSelectedConferenceId(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">-- Select Conference --</option>
          {conferences.map(conf => (
            <option key={conf.id} value={conf.id}>
              {conf.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading papers...</div>}

      {!loading && selectedConferenceId && papers.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>No Papers Found</h3>
          <p>No papers have been submitted for this conference yet.</p>
        </div>
      )}

      {!loading && papers.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Paper ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Title</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Author</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Track</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Submitted</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Reviewers</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {papers.map(paper => (
                <tr key={paper.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{paper.id}</td>
                  <td style={{ padding: '12px' }}>{paper.title}</td>
                  <td style={{ padding: '12px' }}>{paper.authorName}</td>
                  <td style={{ padding: '12px' }}>{paper.trackName}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{formatDate(paper.submissionDate)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(paper.status),
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {paper.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {paper.reviewerNames && paper.reviewerNames.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {paper.reviewerNames.map((name, index) => (
                          <span
                            key={index}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              fontSize: '12px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        No reviewers assigned
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenAssignModal(paper)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Assign Reviewers
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!selectedConferenceId && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>Select a Conference</h3>
          <p>Please select a conference from the dropdown above to view submitted papers.</p>
        </div>
      )}

      {/* Assign Reviewers Modal */}
      {showAssignModal && selectedPaper && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
              <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>
                Assign Reviewers
              </h2>
              <h3 style={{ margin: '5px 0', color: '#666', fontSize: '16px', fontWeight: 'normal' }}>
                {selectedPaper.title}
              </h3>
              <p style={{ margin: '5px 0', color: '#888', fontSize: '14px' }}>
                {abstractMetadata?.visibleToReviewers && selectedPaper.authorName && (
                  <>Author: {selectedPaper.authorName} | </>
                )}
                Track: {selectedPaper.trackName || 'Not specified'}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                fontWeight: 'bold',
                color: '#333'
              }}>
                Select Reviewers:
              </label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '10px'
              }}>
                {reviewers.length === 0 && (
                  <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
                    No reviewers available
                  </p>
                )}
                {reviewers.map(reviewer => (
                  <div
                    key={reviewer.id}
                    style={{
                      padding: '10px',
                      marginBottom: '8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: selectedReviewers.includes(reviewer.id) ? '#e3f2fd' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleReviewerToggle(reviewer.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedReviewers.includes(reviewer.id)}
                        onChange={() => {}}
                        style={{ marginRight: '10px', cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {reviewer.fullName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {reviewer.email}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                {selectedReviewers.length} reviewer(s) selected
              </p>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #f0f0f0',
              paddingTop: '20px'
            }}>
              <button
                type="button"
                onClick={handleCloseAssignModal}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignReviewers}
                disabled={loading || selectedReviewers.length === 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || selectedReviewers.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: loading || selectedReviewers.length === 0 ? 0.6 : 1
                }}
              >
                {loading ? 'Assigning...' : 'Assign Reviewers'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperAssignment;
